import React, { useRef } from "react";
import { Helmet } from "react-helmet-async";
import Footer from "../../components/Footer";
import { useArticlesContext } from "../../context/ArticlesContext";
import MobileMapOverlay from "../../components/MobileMapOverlay";
import SocialMedia from "../../components/SocialMedia";
import Marker from "../../components/Marker";
import Top from "../../components-top/Top";
import Left from "../../components-top/Left";
import Right from "../../components-top/Right";
import Bottom from "../../components-top/Bottom";

export default function ArticlePerfectCroissantVancouver() {
  // 1) Provide a unique reference ID (the slug in your ArticlesContext)
  const ref_id = "perfect-croissant-vancouver";

  // 2) Access articles from your context (optional usage)
  const { articles } = useArticlesContext();
  const ref_data = articles.find((art) => art.slug === ref_id);

  // =========================================================
  // ========== SECTION TITLES IDS (Labels Map) ==============
  // =========================================================
  const labelsMap = {
    intro: "Introduction",
    "section-1": "Beaucoup Bakery & Café",
    "section-2": "L’Atelier Patisserie",
    "section-3": "Bâtard Boulangerie & Café Moderne",
    "section-4": "Le Marché St. George",
    "section-5": "Small Victory Bakery",
    "section-6": "Angus T Bakery & Café",
    "section-7": "Merci Boulangerie",
    "section-8": "Nemesis Coffee (Dope Bakehouse)",
    "section-9": "Thomas Haas Patisserie",
    "section-10": "A Bread Affair",
    faq: "FAQ",
    "local-regulations": "Local Regulations",
    conclusion: "Conclusion",
    references: "References",
  };

  // ===================================
  // 4) References Data
  // ===================================
  const referencesData = [
    {
      url: "https://www.destinationvancouver.com/",
      label: "Destination Vancouver",
    },
    {
      url: "https://www.straight.com/",
      label: "Georgia Straight",
    },
    {
      url: "https://vancouverfoodster.com/",
      label: "Vancouver Foodster",
    },
  ];

  // ===================================
  // 5) Locations for the Leaflet map
  // ===================================
  // Provide as many locations as needed. Each location's "id" must match the
  // ref you use in the corresponding <p> or <li> section. Coordinates go in `lat`/`lng`.
  // Coordinates must be precise to at least 5 decimals as requested.
  const locations = [
    {
      id: "section-1",
      lat: 49.26645,
      lng: -123.14071,
      popupText: "Beaucoup Bakery & Café (Kitsilano)",
    },
    {
      id: "section-2",
      lat: 49.26200,
      lng: -123.09500,
      popupText: "L’Atelier Patisserie (Mount Pleasant)",
    },
    {
      id: "section-3",
      lat: 49.24870,
      lng: -123.08950,
      popupText: "Bâtard Boulangerie & Café Moderne (Fraserhood)",
    },
    {
      id: "section-4",
      lat: 49.24230,
      lng: -123.09600,
      popupText: "Le Marché St. George (Riley Park/Little Mountain)",
    },
    {
      id: "section-5",
      lat: 49.25787,
      lng: -123.13905,
      popupText: "Small Victory Bakery (South Granville)",
    },
    {
      id: "section-6",
      lat: 49.27668,
      lng: -123.11989,
      popupText: "Angus T Bakery & Café (Yaletown)",
    },
    {
      id: "section-7",
      lat: 49.26279,
      lng: -123.06960,
      popupText: "Merci Boulangerie (Commercial Drive)",
    },
    {
      id: "section-8",
      lat: 49.28200,
      lng: -123.10800,
      popupText: "Nemesis Coffee (Dope Bakehouse) (Gastown)",
    },
    {
      id: "section-9",
      lat: 49.26440,
      lng: -123.16813,
      popupText: "Thomas Haas Patisserie (Kitsilano)",
    },
    {
      id: "section-10",
      lat: 49.27135,
      lng: -123.13476,
      popupText: "A Bread Affair (Granville Island)",
    },
  ];

  // Keep references to textual sections for scroll linking
  const spotRefs = useRef({});
  const setSpotRef = (id, el) => {
    spotRefs.current[id] = el;
  };

  // Desktop map reference
  const mapRef = useRef(null);

  // MOBILE MAP
  const mobileMapRef = useRef(null);

  return (
    <>
      {/* ============ SEO & METADATA ============ */}
      <Helmet>
        <title>Where to Find the Perfect Croissant in Vancouver</title>
        <meta
          name="description"
          content="Discover Vancouver's best croissants at top-rated and hidden gem bakeries, complete with practical details, local tips, FAQs, and must-try specialties."
        />
        <meta
          name="keywords"
          content="croissants, vancouver, bakeries, pastry, almond croissant"
        />
        <meta name="robots" content="index, follow" />
        <link
          rel="canonical"
          href="https://www.bcouver.com/articles/perfect-croissant-vancouver.html"
        />

        {/* Open Graph / Facebook */}
        <meta
          property="og:title"
          content="Where to Find the Perfect Croissant in Vancouver"
        />
        <meta
          property="og:description"
          content="Explore a curated list of Vancouver’s best spots for flaky, buttery croissants."
        />
        <meta
          property="og:image"
          content="https://bcouver.com/assets/images/perfect-croissant-vancouver.jpg"
        />
        <meta
          property="og:url"
          content="https://www.bcouver.com/articles/perfect-croissant-vancouver.html"
        />
        <meta property="og:type" content="article" />

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta
          name="twitter:title"
          content="Where to Find the Perfect Croissant in Vancouver"
        />
        <meta
          name="twitter:description"
          content="A handy guide to Vancouver's best croissants, covering addresses, specialties, and FAQs."
        />
        <meta
          name="twitter:image"
          content="https://bcouver.com/assets/images/perfect-croissant-vancouver.jpg"
        />
      </Helmet>

      {/* ============ TOP NAVBAR + HERO ============ */}
      <Top ref_id={ref_id} />

      <main>
        <div className="container article-main">
          {/* LEFT COLUMN (TableOfContents + Map) */}
          <Left
            labelsMap={labelsMap}
            locations={locations}
            mapRef={mapRef}
            spotRefs={spotRefs}
          />

          {/* ============ ARTICLE CONTENT ============ */}
          <article className="article-content">
            {/* Featured Image */}
            <img
              src={
                ref_data?.image ||
                "https://bcouver.com/assets/images/article-fallback.jpg"
              }
              alt="A delicious croissant on a wooden table"
              className="featured-image"
            />

            {/* Social Share */}
            <SocialMedia />

            {/* SECTION: Introduction */}
            <h2 id="intro">Introduction</h2>
            <p>
              Vancouver’s food scene is brimming with international culinary
              delights, but few treats inspire more devotion than a beautifully
              crafted croissant. Whether you crave the classic buttery layers of
              a Parisian-style pastry or want to explore inventive West Coast
              flavors, this guide will show you exactly where to find the{" "}
              <strong>perfect croissant in Vancouver</strong>. We’ve included
              addresses, must-try specialties, approximate costs, and a handful
              of local tips to ensure you get the flakiest, tastiest experience
              possible.
            </p>
            <p>
              Here’s everything you need to know about Vancouver’s top-rated and
              hidden gem bakeries for croissants. Come hungry—and prepared to
              fall in love with these golden delights!
            </p>

            {/* SECTION: Beaucoup Bakery & Café */}
            <h2 id="section-1">Beaucoup Bakery &amp; Café</h2>
            <p
              ref={(el) => setSpotRef("section-1", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>Beaucoup Bakery &amp; Café</strong>{" "}
              <Marker
                coordY={49.26645}
                coordX={-123.14071}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />{" "}
              is a Parisian-inspired café with a chic yet cozy vibe. Located in
              Kitsilano, it’s famous for meticulously laminated croissants that
              strike the ideal balance of flaky exterior and pillowy interior.
            </p>
            <ul>
              <li>
                <strong>Address:</strong> 2150 Fir St, Vancouver, BC V6J 3B5
              </li>
              <li>
                <strong>Operating Hours:</strong> 8:00 AM – 5:00 PM daily
                (check for holiday closures)
              </li>
              <li>
                <strong>Approximate Cost:</strong> $4.50–$6.00 per croissant
              </li>
              <li>
                <strong>Must-Try:</strong> Classic Butter Croissant, Almond
                Croissant, and their seasonal specials
              </li>
            </ul>
            <p>
              Pro tip: Arrive early for the best selection—they often sell out
              by mid-afternoon.
            </p>

            {/* SECTION: L’Atelier Patisserie */}
            <h2 id="section-2">L’Atelier Patisserie</h2>
            <p
              ref={(el) => setSpotRef("section-2", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>L’Atelier Patisserie</strong>{" "}
              <Marker
                coordY={49.26200}
                coordX={-123.09500}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />{" "}
              in Mount Pleasant is a hidden gem known for its{" "}
              <em>textbook-perfect</em> almond croissants. Their French-trained
              pastry chefs keep the focus on authenticity, offering a selection
              of viennoiseries that truly capture the taste of southern France.
            </p>
            <ul>
              <li>
                <strong>Address:</strong> 599 E Broadway, Vancouver, BC V5T 1X4
              </li>
              <li>
                <strong>Operating Hours:</strong> Tue–Sun 8:00 AM – 5:00 PM; Mon
                9:00 AM – 3:00 PM
              </li>
              <li>
                <strong>Approximate Cost:</strong> $4–$7 per croissant (depending
                on filling or flavor)
              </li>
              <li>
                <strong>Must-Try:</strong> Almond Croissant and Chocolate
                Pistachio Croissant
              </li>
            </ul>
            <p>
              Insider tip: They have limited seating, so be prepared to grab and
              go. If you want multiple pastries, ordering ahead is recommended.
            </p>

            {/* SECTION: Bâtard Boulangerie & Café Moderne */}
            <h2 id="section-3">Bâtard Boulangerie &amp; Café Moderne</h2>
            <p
              ref={(el) => setSpotRef("section-3", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>Bâtard Boulangerie &amp; Café Moderne</strong>{" "}
              <Marker
                coordY={49.24870}
                coordX={-123.08950}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />{" "}
              is an award-winning artisanal French bakery in Vancouver’s
              Fraserhood. Famous for their bread and their{" "}
              <em>Double-Baked Almond Croissant</em>, Bâtard combines Old World
              charm with modern café comforts.
            </p>
            <ul>
              <li>
                <strong>Address:</strong> 3958 Fraser St, Vancouver, BC V5V 4E4
              </li>
              <li>
                <strong>Operating Hours:</strong> Wed–Sun 8:00 AM – 4:00 PM;
                closed Mon–Tue
              </li>
              <li>
                <strong>Approximate Cost:</strong> $4.50–$6.50 per croissant
              </li>
              <li>
                <strong>Must-Try:</strong> Double-Baked Almond Croissant, Pain au
                Chocolat
              </li>
            </ul>
            <p>
              This café has indoor seating, making it perfect for a relaxed
              morning indulgence.
            </p>

            {/* SECTION: Le Marché St. George */}
            <h2 id="section-4">Le Marché St. George</h2>
            <p
              ref={(el) => setSpotRef("section-4", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>Le Marché St. George</strong>{" "}
              <Marker
                coordY={49.24230}
                coordX={-123.09600}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />{" "}
              is a cozy neighborhood spot in Riley Park/Little Mountain, beloved
              for its rustic ambiance and scratch-made pastries. Their almond
              croissant garners a cult following, thanks to its house-made almond
              paste and perfect caramelization.
            </p>
            <ul>
              <li>
                <strong>Address:</strong> 4393 St. George St, Vancouver, BC V5V
                4A3
              </li>
              <li>
                <strong>Operating Hours:</strong> Tue–Sun 8:00 AM – 5:00 PM;
                closed Monday
              </li>
              <li>
                <strong>Approximate Cost:</strong> $4–$5 per croissant
              </li>
              <li>
                <strong>Must-Try:</strong> Almond Croissant, Butter Croissant
              </li>
            </ul>
            <p>
              There’s limited seating, so consider enjoying your pastry at a
              nearby park on sunny days.
            </p>

            {/* SECTION: Small Victory Bakery */}
            <h2 id="section-5">Small Victory Bakery</h2>
            <p
              ref={(el) => setSpotRef("section-5", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>Small Victory Bakery</strong>{" "}
              <Marker
                coordY={49.25787}
                coordX={-123.13905}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />{" "}
              has locations in South Granville and beyond. Known for their modern
              aesthetic and innovative baked goods, they offer both regular and
              <em>mini croissants</em> that have pastry fans raving.
            </p>
            <ul>
              <li>
                <strong>Address (South Granville):</strong> 3070 Granville St,
                Vancouver, BC V6H 3J8
              </li>
              <li>
                <strong>Operating Hours:</strong> 8:00 AM – 5:00 PM daily (hours
                vary by location)
              </li>
              <li>
                <strong>Approximate Cost:</strong> $4–$5 per croissant; mini
                croissants ~$2 each
              </li>
              <li>
                <strong>Must-Try:</strong> Almond Croissant, Pain au Chocolat,
                Mini Croissants
              </li>
            </ul>
            <p>
              Pro tip: Ideal for an Instagrammable coffee-and-croissant break.
              Lines can be long on weekends.
            </p>

            {/* SECTION: Angus T Bakery & Café */}
            <h2 id="section-6">Angus T Bakery &amp; Café</h2>
            <p
              ref={(el) => setSpotRef("section-6", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>Angus T Bakery &amp; Café</strong>{" "}
              <Marker
                coordY={49.27668}
                coordX={-123.11989}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />{" "}
              is a croissant specialist in Yaletown. If you’re looking for
              creative twists—like Matcha, Pistachio, or even Tuna &amp; Corn
              croissants—this is the place. Their classic butter croissant is
              also top-tier.
            </p>
            <ul>
              <li>
                <strong>Address:</strong> 1036 Mainland Street, Vancouver, BC V6B
                2T4
              </li>
              <li>
                <strong>Operating Hours:</strong> Mon–Thu 8:00 AM – 4:30 PM;
                Fri–Sun 8:00 AM – 5:30 PM
              </li>
              <li>
                <strong>Approximate Cost:</strong> $4.75–$8 per croissant
              </li>
              <li>
                <strong>Must-Try:</strong> Pistachio Croissant, Tuna &amp; Corn
                (savory croissant), Seasonal Specials
              </li>
            </ul>
            <p>
              Expect unique flavor adventures that push croissant boundaries.
              Limited batches of each flavor daily.
            </p>

            {/* SECTION: Merci Boulangerie */}
            <h2 id="section-7">Merci Boulangerie</h2>
            <p
              ref={(el) => setSpotRef("section-7", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>Merci Boulangerie</strong>{" "}
              <Marker
                coordY={49.26279}
                coordX={-123.06960}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />{" "}
              on Commercial Drive is a newer spot run by a Red Seal baker trained
              in Paris. Their butter croissant’s authentic French flavor has
              garnered near-perfect reviews. They operate with a community focus
              and emphasize small-batch quality.
            </p>
            <ul>
              <li>
                <strong>Address:</strong> 2879 Commercial Drive, Vancouver, BC
                V5N 0A6
              </li>
              <li>
                <strong>Operating Hours:</strong> Thu 7:30 AM – 2:00 PM; Fri–Sat
                7:30 AM – 4:30 PM; Sun 8:00 AM – 4:00 PM
              </li>
              <li>
                <strong>Approximate Cost:</strong> ~$4–$5 per croissant
              </li>
              <li>
                <strong>Must-Try:</strong> Butter Croissant, Almond Croissant,
                seasonal fruit danishes
              </li>
            </ul>
            <p>
              Limited opening days, so get there early or place pre-orders for
              multiple pastries.
            </p>

            {/* SECTION: Nemesis Coffee (Dope Bakehouse) */}
            <h2 id="section-8">Nemesis Coffee (Dope Bakehouse)</h2>
            <p
              ref={(el) => setSpotRef("section-8", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>Nemesis Coffee</strong>{" "}
              <Marker
                coordY={49.28200}
                coordX={-123.10800}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />{" "}
              is primarily a third-wave coffee spot, but its{" "}
              <em>Dope Bakehouse</em> arm churns out some of the city’s most
              creative croissants—think Tiramisu or Mochi fusion croissants. You
              can also find classic almond and butter croissants for purists.
            </p>
            <ul>
              <li>
                <strong>Address:</strong> 302 W Hastings St, Vancouver, BC V6B
                1K6 (Gastown location)
              </li>
              <li>
                <strong>Operating Hours:</strong> Daily 8:00 AM – 4:00 PM
              </li>
              <li>
                <strong>Approximate Cost:</strong> $4–$7 per croissant
              </li>
              <li>
                <strong>Must-Try:</strong> Tiramisu Croissant, Pain au Chocolat
              </li>
            </ul>
            <p>
              Perfect for coffee lovers craving an avant-garde pastry experience.
            </p>

            {/* SECTION: Thomas Haas Patisserie */}
            <h2 id="section-9">Thomas Haas Patisserie</h2>
            <p
              ref={(el) => setSpotRef("section-9", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>Thomas Haas</strong>{" "}
              <Marker
                coordY={49.26440}
                coordX={-123.16813}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />{" "}
              is a legendary pastry chef and chocolatier. His Kitsilano and North
              Vancouver shops are renowned for indulgent treats, especially the
              iconic <em>Double-Baked Almond Croissant</em>, often cited as one
              of Vancouver’s best.
            </p>
            <ul>
              <li>
                <strong>Address (Kitsilano):</strong> 2539 W Broadway, Vancouver,
                BC V6K 2E9
              </li>
              <li>
                <strong>Operating Hours:</strong> Tue–Sat 8:00 AM – 5:30 PM;
                closed Sun–Mon
              </li>
              <li>
                <strong>Approximate Cost:</strong> $4.50–$7.30 per croissant
              </li>
              <li>
                <strong>Must-Try:</strong> Double-Baked Almond Croissant, Double-Baked Chocolate Almond Croissant
              </li>
            </ul>
            <p>
              Expect lines on weekends, but the buttery reward is worth the wait.
            </p>

            {/* SECTION: A Bread Affair */}
            <h2 id="section-10">A Bread Affair</h2>
            <p
              ref={(el) => setSpotRef("section-10", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>A Bread Affair</strong>{" "}
              <Marker
                coordY={49.27135}
                coordX={-123.13476}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />{" "}
              on Granville Island is an organic bakery known for its wide range
              of breads, but don’t overlook the croissants. Their Pain au
              Chocolat gets special mention for its perfectly layered chocolate
              filling and flaky texture.
            </p>
            <ul>
              <li>
                <strong>Address:</strong> 1680 Johnston Street, Vancouver, BC
                V6H 3S2
              </li>
              <li>
                <strong>Operating Hours:</strong> Mon–Fri 9:00 AM – 6:30 PM;
                Sat–Sun 9:00 AM – 7:00 PM
              </li>
              <li>
                <strong>Approximate Cost:</strong> ~$4 per croissant
              </li>
              <li>
                <strong>Must-Try:</strong> Pain au Chocolat, Butter Croissant
              </li>
            </ul>
            <p>
              Tip: If you plan to eat outside, guard your pastry from the
              ever-watchful seagulls of Granville Island!
            </p>

            {/* SECTION: FAQ */}
            <h2 id="faq">FAQ</h2>
            <div className="faq-section">
              <h3>1. Are Vancouver’s croissants really as good as in Paris?</h3>
              <p>
                Many local pastry chefs have trained in France, bringing authentic
                techniques to Vancouver. Reviews consistently praise these
                croissants as on-par (or even better) than some Parisian bakeries,
                thanks to the city’s high-quality ingredients and innovative
                flavor options.
              </p>

              <h3>2. What is a double-baked almond croissant?</h3>
              <p>
                It’s a croissant that’s typically a day old, soaked in syrup,
                filled with almond cream, and baked a second time. The result is
                a richer, caramelized pastry—famous at places like Thomas Haas
                and Bâtard.
              </p>

              <h3>3. Where can I find vegan or gluten-free croissants?</h3>
              <p>
                Traditional croissants use wheat flour and butter, so most spots
                on this list don’t offer vegan/gluten-free versions. However,
                some bakeries in Vancouver experiment with vegan pastry dough or
                gluten-free alternatives. It’s best to call ahead or check their
                websites for any dietary-friendly options.
              </p>
            </div>

            {/* SECTION: Local Regulations */}
            <h2 id="local-regulations">Local Regulations</h2>
            <p>
              While there aren’t specific regulations for purchasing or consuming
              croissants in Vancouver, remember that many bakeries are in
              residential areas or popular tourist zones. Observe parking rules,
              respect local noise bylaws, and properly dispose of your trash to
              support the city’s recycling and composting initiatives.
            </p>

            {/* SECTION: Conclusion */}
            <h2 id="conclusion">Conclusion</h2>
            <p>
              With its blend of French heritage, artisanal skill, and culinary
              creativity, Vancouver is a paradise for croissant lovers. Whether
              you’re sampling a traditional butter croissant at a classic
              patisserie or exploring inventive flavors at a modern café,
              you’ll find top-quality pastries that live up to their gourmet
              reputation. Happy croissant hunting!
            </p>
            <p>
              <em>
                Disclaimer: Hours and availability may change, so always check
                official resources or call ahead before visiting.
              </em>
            </p>

            {/* BOTTOM COMPONENT: references, author, related articles, etc. */}
            <Bottom referencesData={referencesData} ref_data={ref_data} />
          </article>

          {/* RIGHT SIDE COLUMN */}
          <Right currentArticle={ref_data} />
        </div>
      </main>

      {/* MOBILE MAP OVERLAY */}
      <MobileMapOverlay
        ref={mobileMapRef}
        markers={locations}
        spotRefs={spotRefs}
      />

      <Footer />
    </>
  );
}